/**
 * SealLoader — "Loader v3 · Seal" (Claude Design project "iAdvocate Loading
 * Animation", implemented faithfully).
 *
 * The launch animation: a rotating notarial seal — hairline rings, slow tick
 * ring, counter-rotating circular inscription, gold sweeping arc — around an
 * "iA" monogram with a foil sheen; below, the iAdvocate signature reveals
 * itself with a hand-drawn flourish, then status words cycle.
 *
 * Replaces the old scales-and-books video sting as the launch animation.
 * Fonts self-inject (id-guarded) so no index.html changes are needed.
 */
import { useEffect } from "react";

const ACCENT = "#C8A24B";
const INK = "#EFE9D8";
const MUTED = "#8FA396";
const HAIR = "rgba(239,233,216,0.22)";

const FONT_ID = "seal-loader-fonts";
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500&family=Great+Vibes&family=Pinyon+Script&family=Archivo:wght@400;500&display=swap";

function useSealFonts() {
  useEffect(() => {
    if (document.getElementById(FONT_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_ID;
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
    // Deliberately never removed — the splash may remount and fonts are cached.
  }, []);
}

const KEYFRAMES = `
@keyframes ldrSealIn{from{opacity:0;transform:scale(1.1);filter:blur(8px);}to{opacity:1;transform:scale(1);filter:blur(0);}}
@keyframes ldrSpinCW{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
@keyframes ldrSpinCCW{from{transform:rotate(0deg);}to{transform:rotate(-360deg);}}
@keyframes ldrArcSweep{0%{stroke-dasharray:40 840;transform:rotate(-90deg);}50%{stroke-dasharray:340 540;transform:rotate(120deg);}100%{stroke-dasharray:40 840;transform:rotate(270deg);}}
@keyframes ldrFoil{0%{background-position:-180% 0;}55%{background-position:180% 0;}100%{background-position:180% 0;}}
@keyframes ldrFadeUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
@keyframes ldrLineGrow{from{transform:scaleX(0);opacity:0;}to{transform:scaleX(1);opacity:1;}}
@keyframes ldrWordCycle{0%{opacity:0;transform:translateY(5px);}6%{opacity:1;transform:translateY(0);}27%{opacity:1;transform:translateY(0);}33%{opacity:0;transform:translateY(-5px);}100%{opacity:0;}}
@keyframes ldrBreathe{0%,100%{transform:scale(1);}50%{transform:scale(1.015);}}
@keyframes ldrSignReveal{from{clip-path:inset(-30% 100% -30% -5%);}to{clip-path:inset(-30% -8% -30% -5%);}}
@keyframes ldrDrawFlourish{to{stroke-dashoffset:0;}}
`;

interface SealLoaderProps {
  /** Cycling status words; defaults to the design's set. */
  words?: string[];
}

export default function SealLoader({
  words = ["Preparing your counsel", "Criminal", "Civil", "Corporate"],
}: SealLoaderProps) {
  useSealFonts();
  const cycle = Math.max(words.length * 2, 8);
  return (
    <div
      role="status"
      aria-label="Loading iAdvocate"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(130% 100% at 50% 20%, #173A2C 0%, #0A1F16 72%)",
        fontFamily: "'Archivo', sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{KEYFRAMES}</style>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 24px" }}>
        {/* the seal */}
        <div style={{ position: "relative", width: 290, height: 290, animation: "ldrSealIn 1.4s cubic-bezier(0.2,0.6,0.2,1) 0.1s both" }}>
          <div style={{ position: "absolute", inset: 0, animation: "ldrBreathe 7s ease-in-out 2s infinite" }}>
            <svg width="290" height="290" viewBox="0 0 300 300" fill="none" style={{ position: "absolute", inset: 0, overflow: "visible" }}>
              <circle cx="150" cy="150" r="144" stroke={HAIR} strokeWidth="0.8" />
              <circle cx="150" cy="150" r="138" stroke={HAIR} strokeWidth="0.8" />
              {/* tick ring, rotating slowly */}
              <g style={{ transformBox: "view-box", transformOrigin: "150px 150px", animation: "ldrSpinCW 90s linear infinite" } as React.CSSProperties}>
                <circle cx="150" cy="150" r="128" stroke={MUTED} strokeWidth="7" strokeDasharray="1.1 10.06" opacity="0.5" />
              </g>
              {/* circular inscription, counter-rotating */}
              <g style={{ transformBox: "view-box", transformOrigin: "150px 150px", animation: "ldrSpinCCW 120s linear infinite" } as React.CSSProperties}>
                <path id="ldr-inscription" d="M150 44 a106 106 0 1 1 -0.01 0" fill="none" />
                <text fontFamily="Archivo,sans-serif" fontSize="11.5" letterSpacing="4.5" fill={MUTED}>
                  <textPath href="#ldr-inscription" startOffset="0">
                    iADVOCATE · LEGAL INTELLIGENCE · CRIMINAL · CIVIL · CORPORATE ·
                  </textPath>
                </text>
              </g>
              <circle cx="150" cy="150" r="86" stroke={HAIR} strokeWidth="0.8" />
              <circle cx="150" cy="150" r="82" stroke={ACCENT} strokeWidth="0.8" opacity="0.55" />
              {/* loading arc */}
              <circle
                cx="150" cy="150" r="144" stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round" pathLength="880"
                style={{ transformBox: "view-box", transformOrigin: "150px 150px", animation: "ldrArcSweep 3.6s cubic-bezier(0.45,0.05,0.55,0.95) 1.2s infinite" } as React.CSSProperties}
              />
              {/* cardinal diamonds */}
              <path d="M150 2 L154 8 L150 14 L146 8 Z" fill={ACCENT} />
              <path d="M150 286 L154 292 L150 298 L146 292 Z" fill={ACCENT} opacity="0.6" />
            </svg>
            {/* monogram with foil sheen */}
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: 96,
                  lineHeight: 1,
                  paddingBottom: 10,
                  background: `linear-gradient(105deg, #8A6D2F 0%, ${ACCENT} 38%, #F3E3B0 50%, ${ACCENT} 62%, #8A6D2F 100%)`,
                  backgroundSize: "250% 100%",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  animation: "ldrFoil 5s ease-in-out 1.6s infinite",
                }}
              >
                iA
              </div>
            </div>
          </div>
        </div>

        {/* signature wordmark */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 26 }}>
          <div
            style={{
              fontFamily: "'Great Vibes', cursive",
              fontSize: 58,
              lineHeight: 1.1,
              color: INK,
              animation: "ldrSignReveal 2s cubic-bezier(0.4,0.1,0.4,0.9) 1.3s both",
            }}
          >
            <span style={{ color: ACCENT }}>i</span>
            <span style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "1.28em", marginRight: "0.02em" }}>A</span>
            dvocate
          </div>
          <svg width="230" height="18" viewBox="0 0 230 18" fill="none" style={{ marginTop: -2, overflow: "visible" }}>
            <path
              d="M6 10 C60 4 150 4 200 8 C212 9 220 10 224 9 M150 13 C170 15 196 15 214 13"
              stroke={ACCENT} strokeWidth="1.6" strokeLinecap="round" pathLength={1}
              strokeDasharray="1" strokeDashoffset="1"
              style={{ animation: "ldrDrawFlourish 0.9s cubic-bezier(0.4,0,0.3,1) 3.3s both" }}
            />
          </svg>
        </div>

        {/* divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18, animation: "ldrFadeUp 1s ease-out 1.6s both" }}>
          <div style={{ width: 72, height: 1, background: `linear-gradient(90deg, transparent, ${HAIR})`, transformOrigin: "right", animation: "ldrLineGrow 0.9s ease-out 1.7s both" }} />
          <div style={{ width: 5, height: 5, transform: "rotate(45deg)", background: ACCENT }} />
          <div style={{ width: 72, height: 1, background: `linear-gradient(90deg, ${HAIR}, transparent)`, transformOrigin: "left", animation: "ldrLineGrow 0.9s ease-out 1.7s both" }} />
        </div>

        {/* cycling status words */}
        <div style={{ position: "relative", height: 16, width: 260, marginTop: 16 }}>
          {words.map((w, i) => (
            <div
              key={w}
              style={{
                position: "absolute",
                inset: 0,
                textAlign: "center",
                fontSize: 10.5,
                letterSpacing: "0.42em",
                textIndent: "0.42em",
                textTransform: "uppercase",
                color: MUTED,
                opacity: 0,
                animation: `ldrWordCycle ${cycle}s ease-in-out ${2.2 + i * 2}s infinite`,
              }}
            >
              {w}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
